import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLabAccess } from "../../contexts/LabAccessContext";
import "./LabPage.css";

function LabPage({user}) {
  const { labId } = useParams();
  const navigate = useNavigate();

  const {
    labs,
    loading,
    openLab,
    hasLabAccess,
  } = useLabAccess();
  console.log("LABS::::", labs);

  const isLoggedIn = Boolean(user);

  const lab = Array.isArray(labs)
    ? labs.find(item => String(item.id) === String(labId))
    : null;

  if (loading) {
    return (
      <div className="lab-not-found">
        <h1>Loading Lab...</h1>
        <p>Loading laboratory information.</p>
      </div>
    );
  }

  if (!lab) {
    return (
      <div className="lab-not-found">
        <h1>Lab Not Found</h1>
        <p>The requested robotics laboratory does not exist.</p>

        <button onClick={() => navigate("/rect/labs")}>
          Browse Labs
        </button>
      </div>
    );
  }

  const hasAccess =
    isLoggedIn && typeof hasLabAccess === "function"
      ? hasLabAccess(lab.id)
      : false;

  const technologies = Array.isArray(lab.technologies)
    ? lab.technologies
    : [];

  const learning = Array.isArray(lab.learning)
    ? lab.learning
    : [];

  const experiments = Array.isArray(lab.experiments)
    ? lab.experiments
    : [];

  const plans = Array.isArray(lab.plans)
    ? lab.plans
    : [];

  const currentPlan =
    plans.find(plan => plan.current === true) || null;

  const paidPlans = plans.filter(
    plan =>
      plan.price !== null &&
      plan.price !== undefined &&
      Number(plan.price) > 0
  );

  const startingPrice = paidPlans.length
    ? Math.min(...paidPlans.map(plan => Number(plan.price)))
    : null;

  const image =
    lab.image_url || "/static/mdb/img/default-lab.jpg";

  const labPath = `/rect/labs/${lab.slug}`;
  const pricingPath = `/rect/app/labs/pricing/${lab.id}`;

  const goToPricing = () => {
    navigate(pricingPath, {
      state: {
        from: labPath,
      },
    });
  };

  const handleLabAction = () => {
    /*
     * User is not logged in.
     * Let them view the lab, but require login before launch.
     */
    if (!isLoggedIn) {
      navigate("/rect/login", {
        state: {
          from: labPath,
        },
      });

      return;
    }

    /*
     * User is logged in but does not have access.
     */
    if (!hasAccess) {
      goToPricing();
      return;
    }

    /*
     * User is logged in and has access.
     */
    if (typeof openLab !== "function") {
      console.error(
        "LabAccessContext.openLab is not available."
      );

      return;
    }

    // openLab(String(lab.id), {
    //   from: labPath,
    // });

     navigate(`/rect/app/projects/${lab.id}`);
  };

  const getActionText = () => {
    if (!isLoggedIn) {
      return "Sign In to Start";
    }

    if (!hasAccess) {
      return "Get Access";
    }

    return `Launch ${lab.name}`;
  };

  return (
    <div className="lab-page">

      {/* HERO */}
      <section className="lab-hero">
        <div className="lab-hero-content">

          <div className="lab-breadcrumb">
            <button onClick={() => navigate("/rect/labs")}>
              Labs
            </button>

            <span>/</span>

            <span>{lab.name}</span>
          </div>

          <div className="lab-hero-grid">

            {/* HERO TEXT */}
            <div className="lab-hero-text">

              <div className="lab-provider">
                {lab.organization ||
                  lab.category ||
                  "STEMX365"}
              </div>

              <h1>{lab.name}</h1>

              <h2>
                {lab.tagline ||
                  "Explore space robotics."}
              </h2>

              <p className="lab-description">
                {lab.description}
              </p>

              {technologies.length > 0 && (
                <div className="lab-technologies">
                  {technologies.map(technology => (
                    <span
                      key={technology}
                      className="technology-tag"
                    >
                      {technology}
                    </span>
                  ))}
                </div>
              )}

              {/* <div className="lab-action-area">

                {!hasAccess &&
                  startingPrice !== null && (
                    <div className="lab-price">
                      <span className="price">
                        ${startingPrice}
                      </span>

                      <span className="price-period">
                        / month
                      </span>
                    </div>
                  )}

                <button
                  className="lab-primary-button"
                  onClick={handleLabAction}
                >
                  {getActionText()}
                </button>

              </div>  */}

              <p className="secure-payment">
                {hasAccess
                  ? currentPlan
                    ? `Your ${currentPlan.name} subscription is active.`
                    : "Your laboratory access is active."
                  : isLoggedIn
                    ? "Choose a plan to access this laboratory."
                    : "Sign in to get started."}
              </p>
              

            </div>

            {/* HERO IMAGE */}
            <div className="lab-hero-image">
              <img
                src={image}
                alt={lab.name}
              />
            </div>

          </div>
        </div>
      </section>

      {/* OVERVIEW */}
      <section className="lab-section lab-section-dark2">
        <div className="lab-container">

          <div className="section-heading">
            <span>THE LAB</span>

            <h2>
              Explore {lab.name}
            </h2>

            <p>
              Learn by programming a real
              space robotics platform through
              an interactive simulation environment.
            </p>
          </div>

          <div className="lab-overview-grid">

            <div className="overview-card">
              <div className="overview-icon">
                <img
                  src={image}
                  width="20%"
                  alt={lab.name}
                />
              </div>

              <h3>Space Robot</h3>

              <p>
                Work with a robotic platform
                designed for operation in space.
              </p>
            </div>

            <div className="overview-card">
              <div className="overview-icon">
                💻
              </div>

              <h3>Write Code</h3>

              <p>
                Develop your own robot control
                programs using a real programming
                environment.
              </p>
            </div>

            <div className="overview-card">
              <div className="overview-icon">
                🛰️
              </div>

              <h3>Run Simulations</h3>

              <p>
                Test your programs in a
                physics-based robotics simulation.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* LEARNING */}
      {learning.length > 0 && (
        <section className="lab-section lab-section-dark">
          <div className="lab-container">

            <div className="section-heading">
              <span>LEARNING</span>

              <h2>
                What you'll learn
              </h2>
            </div>

            <div className="learning-grid">
              {learning.map((item, index) => (
                <div
                  className="learning-item"
                  key={index}
                >
                  <div className="checkmark">
                    ✓
                  </div>

                  <p>{item}</p>
                </div>
              ))}
            </div>

          </div>
        </section>
      )}

      {/* EXPERIMENTS */}
      {experiments.length > 0 && (
        <section className="lab-section lab-section-dark2">
          <div className="lab-container">

            <div className="section-heading">
              <span>EXPERIMENTS</span>

              <h2>
                Learn through experiments
              </h2>

              <p>
                Progress from basic robot control
                to autonomous missions.
              </p>
            </div>

            <div className="experiments">

              {experiments.map(
                (experiment, index) => (
                  <div
                    className="experiment-card"
                    key={
                      experiment.number ?? index
                    }
                  >

                    <div className="experiment-number">
                      {String(
                        experiment.number ??
                          index + 1
                      ).padStart(2, "0")}
                    </div>

                    <div className="experiment-content">
                      <h3>
                        {experiment.title}
                      </h3>

                      <p>
                        {experiment.description}
                      </p>
                    </div>

                    <div className="experiment-lock">
                      🔒
                    </div>

                  </div>
                )
              )}

            </div>
          </div>
        </section>
      )}

      {/* PLANS */}
      {/* {!hasAccess && plans.length > 0 && (
        <section className="lab-section lab-section-dark">
          <div className="lab-container">

            <div className="section-heading">
              <span>PLANS</span>

              <h2>
                Choose your access
              </h2>

              <p>
                Select the plan that fits
                your {lab.name} mission.
              </p>
            </div>

            <div className="lab-overview-grid">

              {plans.map(plan => (
                <div
                  className="overview-card"
                  key={plan.id}
                >

                  {plan.popular && (
                    <div className="popular-badge">
                      MOST POPULAR
                    </div>
                  )}

                  {plan.current && isLoggedIn && (
                    <div className="current-plan-badge">
                      CURRENT PLAN
                    </div>
                  )}

                  <h3>{plan.name}</h3>

                  <p>
                    {plan.description}
                  </p>

                  <div className="lab-price">

                    {plan.price !== null &&
                    plan.price !== undefined ? (
                      <>
                        <span className="price">
                          ${Number(plan.price).toFixed(0)}
                        </span>

                        <span className="price-period">
                          / month
                        </span>
                      </>
                    ) : (
                      <span className="price">
                        Custom
                      </span>
                    )}

                  </div>

                  <ul className="plan-features">
                    {(plan.features || []).map(
                      (feature, index) => (
                        <li key={index}>
                          <span className="check">
                            ✓
                          </span>

                          {feature}
                        </li>
                      )
                    )}
                  </ul>

                  {!plan.current && (
                    <button
                      className="lab-primary-button"
                      onClick={goToPricing}
                    >
                      {plan.price === null
                        ? "Contact Us"
                        : isLoggedIn
                          ? "Choose Plan"
                          : "Sign In to Continue"}
                    </button>
                  )}

                </div>
              ))}

            </div>
          </div>
        </section>
      )} */}

      {/* HOW IT WORKS */}
      <section className="lab-section how-section">
        <div className="lab-container">

          <div className="section-heading">
            <span>HOW IT WORKS</span>

            <h2>
              Start your robotics mission
            </h2>
          </div>

          <div className="steps">

            <div className="step">
              <div className="step-number">
                1
              </div>

              <h3>Get Access</h3>

              <p>
                Choose a lab plan and activate
                your access through secure
                Stripe checkout.
              </p>
            </div>

            <div className="step">
              <div className="step-number">
                2
              </div>

              <h3>Write Code</h3>

              <p>
                Open your robotics workspace
                and develop your robot program.
              </p>
            </div>

            <div className="step">
              <div className="step-number">
                3
              </div>

              <h3>Run Your Mission</h3>

              <p>
                Build your program and run it
                in the robotics simulation
                environment.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* FINAL CTA 
      <section className="lab-cta">
        <div className="lab-container">

          <h2>
            Ready to explore {lab.name}?
          </h2>

          <p>
            Start programming your own space robot.
          </p>

          <button
            className="lab-primary-button"
            onClick={handleLabAction}
          >
            {getActionText()}
          </button>

        </div>
      </section>
      */}

    </div>
  );
}

export default LabPage;